// Prepares scans for the กระทบยอด ปกส. OCR in the browser.
// A PDF's pages are rendered to PNG images: the OCR review shows these (the CSP blocks framing a PDF)
// and the backend sends them to the vision model (handed a scanned PDF directly, it invented rows).
// pdf.js is loaded on demand; its worker entry runs it on the main thread, so no Worker or blob:
// script is needed under the CSP. Large photos are shrunk to fit the API's request size.

const TARGET_WIDTH = 2000; // px — enough for the small print of an SSO patient table

/** @returns {Promise<Array<{ blob: Blob, url: string }>>} one PNG or JPEG per page, first `maxPages` pages */
export async function renderPdfPages(file, { maxPages = 10 } = {}) {
  const [pdfjsLib] = await Promise.all([import("pdfjs-dist/build/pdf"), import("pdfjs-dist/build/pdf.worker.entry")]);
  // isEvalSupported: false — the CSP has no unsafe-eval
  const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(await file.arrayBuffer()), isEvalSupported: false }).promise;
  const pages = [];
  for (let n = 1; n <= Math.min(pdf.numPages, maxPages); n++) {
    const page = await pdf.getPage(n);
    const viewport = page.getViewport({ scale: TARGET_WIDTH / page.getViewport({ scale: 1 }).width });
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(viewport.width);
    canvas.height = Math.round(viewport.height);
    const context = canvas.getContext("2d");
    context.fillStyle = "#fff"; // scans with transparent areas read as black otherwise
    context.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: context, viewport }).promise;
    // whichever is smaller: a clean scan compresses best as PNG, a grey or photographed one as JPEG
    const [png, jpeg] = await Promise.all([
      new Promise((resolve) => canvas.toBlob(resolve, "image/png")),
      new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.85)),
    ]);
    const blob = png.size <= jpeg.size ? png : jpeg;
    pages.push({ blob, url: URL.createObjectURL(blob) });
  }
  return pages;
}

const MAX_IMAGE_SIDE = 2400; // px — phone photos are 4000+ px and several MB; the API takes ~6 MB per request
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;

/**
 * A photo/scan ready to upload: returned as is when small, otherwise re-encoded as JPEG with its long
 * side at MAX_IMAGE_SIDE (still sharp enough for the table's digits).
 * @returns {Promise<Blob>}
 */
export async function shrinkImage(file) {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_IMAGE_SIDE / Math.max(bitmap.width, bitmap.height));
  if (scale === 1 && file.size <= MAX_IMAGE_BYTES) {
    bitmap.close();
    return file;
  }
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.9));
}

// The API takes about 6 MB per request and binary bodies grow by a third in transit.
export const MAX_UPLOAD_BYTES = 4.5 * 1024 * 1024;
